import { MemberPermission } from "@swivl-mono/types";
import { useEffect, useState } from "react";

import LocationsModel from "../../Models/Locations/Locations.Model";
import { Member } from "../../Models/Member/Member.Current";
import { LocationPicker } from "./Components/LocationPicker";
import { RenderTotalsAndChart } from "./Helpers/ChartRenderer";
import { useBotAnalyticsData } from "./Helpers/useBotAnalyticsData";
import { CallToAction } from "./Renders/CallToAction";
import { AllCortex, Cortex } from "./Renders/Cortex";
import { Discounts } from "./Renders/Discounts";
import { FMSLeads } from "./Renders/FMS";
import { Intent } from "./Renders/Intent";
import { Inventory } from "./Renders/Inventory";
import { Automation } from "./Renders/NLPAutomation";
import { PayBill } from "./Renders/PayBill";
import { ProfileCollect } from "./Renders/ProfileCollect";
import { RatingSummary } from "./Renders/RatingSummary";
import { Reviews } from "./Renders/Reviews";
import { TopReviewLocationsComponent } from "./Renders/TopReviewLocations";
import { UpdatedReviews } from "./Renders/UpdatedReviews";
import { User } from "./Renders/User";
import { ViewAllReviewLocations } from "./Renders/ViewAllReviewLocations";
import { ViewAllSequences } from "./Renders/ViewAllSequences";

export enum MenuOption {
  Day = "Day",
  TimeOfDay = "TimeOfDay",
  DayOfWeek = "DayOfWeek"
}
export function TitleForMenuOption(option: MenuOption): string {
  switch (option) {
    case MenuOption.Day:
      return "Day";
    case MenuOption.TimeOfDay:
      return "Time of Day";
    case MenuOption.DayOfWeek:
      return "Day of Week";
  }
}

export function BlankState() {
  return <div className="AnalyticsBlankState">No Data to Show</div>;
}

const colors = ["#ffa904", "#ff940e", "#ff7d1b", "#ff6528", "#ff4b34", "#000000", "#333333", "#666666", "#999999", "#CCCCCC"];

export const BotAnalyticsDashboard = ({ orgId, startDay, endDay }) => {
  const [menuOptions, setMenuOptions] = useState({
    automationMenu: "Day",
    userMenu: "Day",
    profileMenu: "Day",
    sequencesMenu: "Day",
    reviewMenu: "Day",
    updatedReviewMenu: "Day"
  });

  const [locationPermission, setLocationPermission] = useState<MemberPermission>({
    id: "dashboard-filter", // Placeholder ID for filtering purposes
    roleSlug: "dashboard-filter", // Placeholder role for filtering purposes
    locations: { orgId: Member.orgId } // Default to "All Locations"
  });

  const data = useBotAnalyticsData(orgId, startDay, endDay, menuOptions, locationPermission);
  const [showAllCortexes, setShowAllCortexes] = useState(false);
  const [showAllReviewLocations, setShowAllReviewLocations] = useState(false);
  const [showAllSequences, setShowAllSequences] = useState(false);

  const handleMenuChange = (menuType: string, value: string) => {
    setMenuOptions((prev) => {
      const newOptions = { ...prev, [menuType]: value };
      return newOptions;
    });
  };

  const handleLocationChange = (updatedPermission: MemberPermission) => {
    setLocationPermission(updatedPermission);
  };

  // Load locations data when component mounts
  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  if (showAllCortexes) {
    return (
      // fullscreen-overlay has not been added to CSS
      <div className="fullscreen-overlay">
        <AllCortex cortexData={data.cortexData} onViewLess={() => setShowAllCortexes(false)} />
      </div>
    );
  }

  if (showAllReviewLocations) {
    return (
      <div className="fullscreen-overlay">
        <ViewAllReviewLocations
          reviewData={data.reviewData}
          locationNames={data.locationNames}
          onViewLess={() => setShowAllReviewLocations(false)}
          startDay={startDay}
          endDay={endDay}
          loadReviewData={data.loadReviewData}
        />
      </div>
    );
  }

  if (showAllSequences) {
    return (
      <ViewAllSequences
        rawSequencesData={data.rawSequencesData}
        sequencesData={data.sequencesData}
        sequencesMenu={menuOptions.sequencesMenu}
        onMenuChange={handleMenuChange}
        onViewLess={() => setShowAllSequences(false)}
        startDay={startDay}
        endDay={endDay}
      />
    );
  }

  const isLocationFilterActive = !locationPermission.locations?.orgId;

  return (
    <div className="BotAnalyticsDashboard">
      {/* Location Picker Header */}
      <div className="dashboardHeader">
        <LocationPicker locationPermission={locationPermission} updatePermissions={handleLocationChange} />
      </div>

      <div className="chartBoxes topRow">
        <User
          userData={data.userData}
          userMenu={menuOptions.userMenu}
          onMenuChange={handleMenuChange}
          renderTotalsAndChart={RenderTotalsAndChart}
          className={isLocationFilterActive ? "chartNotFilterable" : ""}
        />
        <Automation
          nlpData={data.nlpData}
          automationMenu={menuOptions.automationMenu}
          onMenuChange={handleMenuChange}
          renderTotalsAndChart={RenderTotalsAndChart}
          className={isLocationFilterActive ? "chartNotFilterable" : ""}
          startDay={startDay}
          endDay={endDay}
        />
      </div>
      <div className="chartBoxes">
        <ProfileCollect
          profileData={data.profileCollectData}
          profileMenu={menuOptions.profileMenu}
          onMenuChange={handleMenuChange}
          renderTotalsAndChart={RenderTotalsAndChart}
          startDay={startDay}
          endDay={endDay}
        />
        <FMSLeads fmsLeadData={data.fmsLeadData} colors={colors} />
      </div>
      <div className="chartBoxes">
        <Inventory inventoryData={data.inventoryData} colors={colors} />
        <PayBill payBillData={data.payBillData} colors={colors} />
      </div>
      <div className="chartBoxes">
        <Intent
          nlpData={data.nlpData}
          onViewAll={() => {
            // log("View all intents clicked")
          }}
          className={isLocationFilterActive ? "chartNotFilterable" : ""}
        />
        <Cortex cortexData={data.cortexData} colors={colors} onViewAll={() => setShowAllCortexes(true)} />
      </div>
      <div className="chartBoxes">
        <Discounts discountsData={data.discountsData} colors={colors} />
        <CallToAction callToActionData={data.callToActionData} colors={colors} />
      </div>
      <div className="chartBoxes">
        <Reviews
          reviewData={data.reviewData}
          reviewMenu={menuOptions.reviewMenu}
          renderTotalsAndChart={RenderTotalsAndChart}
          onMenuChange={handleMenuChange}
          loadReviewData={data.loadReviewData}
        />
        <UpdatedReviews
          reviewData={data.reviewData}
          updatedReviewMenu={menuOptions.updatedReviewMenu}
          renderTotalsAndChart={RenderTotalsAndChart}
          onMenuChange={handleMenuChange}
          loadReviewData={data.loadReviewData}
        />
      </div>
      <div className="chartBoxes">
        <RatingSummary reviewData={data.reviewData} loadReviewData={data.loadReviewData} />
        <TopReviewLocationsComponent
          reviewData={data.reviewData}
          locationNames={data.locationNames}
          loadReviewData={data.loadReviewData}
          onViewAll={() => setShowAllReviewLocations(true)}
        />
      </div>
      {/* <div className="chartBoxes">
                <Sequences
                    sequencesData={data.sequencesData}
                    sequencesMenu={menuOptions.sequencesMenu}
                    onMenuChange={handleMenuChange}
                    onViewAll={() => setShowAllSequences(true)}
                    channelFilter={undefined}
                    title={undefined}
                    rawSequencesData={data.rawSequencesData}
                    showTypeOption={false}
                    tableId={undefined}
                    showClickSegments={false}
                />
            </div> */}
    </div>
  );
};
